/** @format */

import { faBoxOpen } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useState } from 'react';

import { vatRates } from '../../../../../utils/constants';
import { moneyFormatter } from '../../../../../utils/numbers';
import { PRODUCT_DOC_ICONS } from '../ProductDocumentActionButtons';
import { usePrice, useProductDetail, useTypologyDetail } from './catalogData';
import {
  DOCUMENT_TYPES,
  getKeyLine,
  getPriceSource,
  getProductName,
} from './catalogLogic';
import DocumentButton from './DocumentButton';
import InfoDot from './InfoDot';

const VAT_RATES = [5.5, 10, 20];

const DOC_ICONS = Object.fromEntries(
  PRODUCT_DOC_ICONS.map(({ type, icon }) => [type, icon]),
);

function useInView(ref) {
  const [inView, setInView] = useState(
    typeof IntersectionObserver === 'undefined',
  );
  useEffect(() => {
    if (inView || !ref.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setInView(true),
      { rootMargin: '200px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [inView]);
  return inView;
}

export function useFailedImages() {
  const [failed, setFailed] = useState([]);
  return [failed, (src) => setFailed((list) => [...list, src])];
}

export function ProductVisual({ product, height }) {
  const [failed, onError] = useFailedImages();
  const kitImages = product.is_kit
    ? (product.kit_lines || [])
        .map((line) => line.product?.image)
        .filter(Boolean)
        .slice(0, 2)
    : [];
  const images = (product.image ? [product.image] : kitImages).filter(
    (src) => !failed.includes(src),
  );
  if (!images.length) {
    return (
      <Box
        sx={{
          height,
          bgcolor: 'grey.100',
          color: 'grey.500',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: 14,
        }}
      >
        <FontAwesomeIcon icon={faBoxOpen} size="2x" />
        Sans photo
      </Box>
    );
  }
  return (
    <Box
      sx={{ height, display: 'flex', gap: 1, bgcolor: 'common.white', p: 1 }}
    >
      {images.map((src) => (
        <Box
          key={src}
          component="img"
          src={src}
          alt=""
          loading="lazy"
          onError={() => onError(src)}
          sx={{ flex: 1, minWidth: 0, height: '100%', objectFit: 'contain' }}
        />
      ))}
    </Box>
  );
}

ProductVisual.propTypes = {
  product: PropTypes.shape({
    image: PropTypes.string,
    is_kit: PropTypes.bool,
    kit_lines: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  height: PropTypes.number.isRequired,
};

export function BrandMark({ brand, height }) {
  const [failed, onError] = useFailedImages();
  if (!brand) return <Box sx={{ height }} />;
  return (
    <Box sx={{ height, display: 'flex', alignItems: 'center' }}>
      {brand.image && !failed.includes(brand.image) ? (
        <Box
          component="img"
          src={brand.image}
          alt={brand.name}
          onError={() => onError(brand.image)}
          sx={{ maxHeight: height, maxWidth: 120, objectFit: 'contain' }}
        />
      ) : (
        <Typography
          variant="body2"
          fontWeight={700}
          color="text.secondary"
          noWrap
        >
          {brand.name}
        </Typography>
      )}
    </Box>
  );
}

BrandMark.propTypes = {
  brand: PropTypes.shape({ image: PropTypes.string, name: PropTypes.string }),
  height: PropTypes.number.isRequired,
};

BrandMark.defaultProps = { brand: null };

export function PriceExplanation({ product, typology }) {
  const detail = useTypologyDetail(product.typology?.slug);
  const price = usePrice(product, false);
  if (!detail || detail.status === 'loading') {
    return <Typography variant="body2">Chargement…</Typography>;
  }
  const source = getPriceSource(product, detail.data || typology);
  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        D&apos;où vient ce prix ?
      </Typography>
      <Typography variant="body2">
        Prix de vente HT calculé par le CRM pour ce produit seul, hors devis.
        Source : {source.label.toLowerCase()}.
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>
        Prix de vente TTC
      </Typography>
      {VAT_RATES.map((rate) => (
        <Box
          key={rate}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography variant="body2">TVA {vatRates[rate]}</Typography>
          <Typography
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {price?.status === 'done'
              ? `${moneyFormatter(price.value * (1 + rate / 100))} € TTC`
              : '—'}
          </Typography>
        </Box>
      ))}
    </>
  );
}

PriceExplanation.propTypes = {
  product: PropTypes.shape({}).isRequired,
  typology: PropTypes.shape({}),
};

PriceExplanation.defaultProps = { typology: null };

export function priceText(product, price) {
  if (!product.is_active) return 'Prix non calculé';
  if (!price || price.status === 'loading') return '… €';
  if (price.status === 'error') return 'Prix indisponible';
  return `${moneyFormatter(price.value)} € HT`;
}

function CatalogCard({ product, brand, typology, selected, onOpen }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const price = usePrice(product, inView);
  const detail = useProductDetail(product.id, inView);
  // Light index data right away, full product (photo, details, documents) once loaded.
  const full = detail ? { ...detail, is_active: product.is_active } : null;
  const name = getProductName(product);
  const kitCount = full?.kit_lines?.length || 0;

  return (
    <Paper
      ref={ref}
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-label={name}
      onClick={() => onOpen(product)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(product);
      }}
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        borderColor: selected ? 'primary.main' : 'grey.300',
        boxShadow: selected
          ? (t) => `0 0 0 2px ${t.palette.primary.main}`
          : 'none',
        transition: 'box-shadow .15s',
        '&:hover, &:focus-visible': {
          boxShadow: (t) => `0 0 0 2px ${t.palette.primary.light}`,
          outline: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {full ? (
          <ProductVisual product={full} height={140} />
        ) : (
          <Skeleton variant="rectangular" height={140} animation="wave" />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'flex',
            gap: 0.5,
          }}
        >
          {!product.is_active && <Chip size="small" label="Inactif" />}
          {full?.is_kit && (
            <Chip
              size="small"
              color="primary"
              label={`Kit · ${kitCount} élément${kitCount > 1 ? 's' : ''}`}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          p: 2,
          pt: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flex: 1,
        }}
      >
        <BrandMark brand={brand || full?.brand || null} height={24} />
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.35,
            minHeight: '2.7em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={name}
        >
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {full ? getKeyLine(full) : <Skeleton width="70%" />}
        </Typography>
        <Box
          sx={{
            mt: 'auto',
            pt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: 16 }}
            color={product.is_active ? 'text.primary' : 'text.secondary'}
          >
            {priceText(product, price)}
          </Typography>
          {product.is_active && (
            <InfoDot label="D'où vient ce prix ?">
              <PriceExplanation product={product} typology={typology} />
            </InfoDot>
          )}
        </Box>
      </Box>
      {/* Own band under a divider; px 0.5 lines the icons up with the text above */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          minHeight: 44,
          px: 0.5,
          py: 0.5,
          borderTop: 1,
          borderColor: 'grey.200',
        }}
      >
        {DOCUMENT_TYPES.filter(({ type }) => full?.[type]?.length).map(
          ({ type, label }) => (
            <DocumentButton
              key={type}
              docs={full[type]}
              label={label}
              icon={DOC_ICONS[type]}
            />
          ),
        )}
      </Box>
    </Paper>
  );
}

CatalogCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    is_active: PropTypes.bool,
    is_kit: PropTypes.bool,
    kit_lines: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  brand: PropTypes.shape({}),
  typology: PropTypes.shape({}),
  selected: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
};

CatalogCard.defaultProps = { brand: null, typology: null, selected: false };

export default memo(CatalogCard);
